// SYNCHRONIZED PRODUCTS - OPTIMIZED VERSION
// This script synchronizes quantities between smartvibes and apollo-wearable
// Cart recommendations are RE-ENABLED with smart reloading
// Price updates and events are ENABLED for immediate UI response

class SynchronizedProducts extends HTMLElement {
  constructor() {
    super();
    
    // Product handles to synchronize
    // Re-enable both products now that cart-items interference is blocked
    this.syncedProductHandles = ['smartvibes', 'apollo-wearable'];
    
    // DELETION BEHAVIOR OPTIONS:
    // 'wearable_master' - apollo-wearable (MASTER): If deleted, also removes smartvibes; smartvibes (DEPENDENT): If deleted, apollo-wearable remains (DEFAULT)
    // 'bidirectional' - If either product is deleted, the other is also deleted
    // 'independent' - Products can be deleted independently without affecting each other
    this.deletionBehavior = this.getDeletionBehaviorFromAddon() || 'wearable_master';
    this.isUpdating = false; // Prevent infinite loops
    this.isAutomatedDeletion = false; // Flag to track programmatic deletions
    this.pendingDependentRemoval = null; // Track pending dependent product removal
    this.updateAttempts = 0; // Track update attempts to prevent infinite loops
    this.lastRecommendationFix = 0; // Track last recommendation fix attempt
    this.lastCartProductId = null; // Store product ID for recommendations after cart close
    this.lastCartClose = 0; // Track last cart close to prevent spam
    this.lastCartOpen = 0; // Track last cart open to prevent spam
    this.maxUpdateAttempts = 3; // Maximum number of update attempts
    this.priceUpdateInProgress = false; // Prevent price update conflicts
    this.inputTimeout = null; // For debouncing input events
    this.cartUpdateCount = 0; // Circuit breaker for infinite loops
    this.lastCartUpdateTime = 0; // Track timing of cart updates
    this.lockedInputs = new Set(); // Track inputs that are temporarily locked
    this.syncInProgress = false; // Track when quantity sync is happening
    this.priceGuardInterval = null; // Track the price guard interval
    this.originalFormatMoney = null; // Store original theme formatMoney function
    
    // SynchronizedProducts system initialized
    this.init();
  }

  init() {
    // Only initialize if we're in the mini-cart context, not on main cart page
    const isInMiniCart = this.closest('mini-cart') || this.closest('.mini-cart') || this.closest('form.mini-cart');
    const isMainCartPage = document.body.classList.contains('template-cart');
    const isInCartItems = this.closest('cart-items');
    
    if (isMainCartPage && !isInMiniCart) {
      return;
    }
    
    // Check if PUB_SUB_EVENTS is available
    if (typeof PUB_SUB_EVENTS === 'undefined') {
      setTimeout(() => this.init(), 1000);
      return;
    }
    
    // Check if subscribe function is available
    if (typeof subscribe === 'undefined') {
      setTimeout(() => this.init(), 1000);
      return;
    }
    
    // DISABLED: Cart update syncing to prevent external interference
    // Only rely on direct quantity input changes for now
    // this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    
    // Listen for cart errors to reset flags
    this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));
    
    // ADDITIONAL CART MONITORING for deletion detection
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Check if any cart items were removed
          if (mutation.removedNodes.length > 0) {
            mutation.removedNodes.forEach((node) => {
              if (node.nodeType === Node.ELEMENT_NODE && node.matches && node.matches('[data-handle]')) {
                const removedHandle = node.dataset.handle;
                
                // If apollo-wearable was removed and we have pending removal
                if (removedHandle === 'apollo-wearable' && this.pendingDependentRemoval) {
                  const dependentHandle = this.pendingDependentRemoval;
                  this.pendingDependentRemoval = null;
                  setTimeout(() => {
                    this.removeDependentProduct(dependentHandle);
                  }, 300);
                }
              }
            });
          }
        }
      });
    });
    
    // Observe the cart items container
    const cartContainer = this.querySelector('[data-animate]') || this;
    observer.observe(cartContainer, { childList: true, subtree: true });
    this.cartObserver = observer; // Store for cleanup
    
    
    // MONITOR CART DRAWER CLOSE EVENTS
    this.setupCartCloseMonitoring();
    
    // CRITICAL: Check if cart is already open on page load and update prices if needed
    // Use a flag to prevent multiple instances from triggering this
    if (!window.CART_PRICES_INITIALIZED) {
      window.CART_PRICES_INITIALIZED = true;
      
      setTimeout(() => {
        const cartDrawer = document.querySelector('cart-drawer details[open]');
        if (cartDrawer) {
          const cartItems = document.querySelectorAll('[data-handle]');
          const hasSyncedProducts = Array.from(cartItems).some(item => 
            this.syncedProductHandles.includes(item?.dataset?.handle)
          );
          
          if (hasSyncedProducts) {
            this.updateVisiblePrices();
          }
        }
      }, 800); // Wait for cart to fully render
    }
    
    // SMART DELETION: Handle asymmetric deletion behavior
    this.addEventListener('click', (e) => {
      // Skip processing if this is an automated deletion
      if (this.isAutomatedDeletion) {
        return;
      }
      
      // Check for remove button clicks - updated selectors for mini-cart
      if (e.target.closest('cart-remove-button') || 
          e.target.closest('.delete-product') || 
          e.target.closest('[data-cart-remove]') || 
          e.target.closest('.cart-remove')) {
        
        const cartItem = e.target.closest('[data-handle]');
        if (cartItem) {
          const handle = cartItem.dataset.handle;
          this.handleProductDeletion(handle);
        }
      }
    });

    // MONITOR QUANTITY CHANGES: Watch for quantity going to 0 (another way to "delete")
    this.addEventListener('input', (e) => {
      if (e.target.matches('input[name="updates[]"]')) {
        const handle = e.target.closest('[data-handle]')?.dataset.handle;
        if (this.syncedProductHandles.includes(handle)) {
          const newQty = parseInt(e.target.value) || 0;
          
          // Skip automated deletion processing if this is an automated change
          if (!this.isAutomatedDeletion) {
            // Check for deletion via quantity = 0
            if (newQty === 0) {
              this.handleProductDeletion(handle);
            }
          }
          
          // Track prices and suppress errors
          setTimeout(() => {
            this.suppressQuantityErrors();
          }, 100);
        }
      }
    });

    // Add change event listener to inputs - listen for both change and input events
    this.addEventListener('change', (e) => {
      console.log('Change event detected:', {
        tagName: e.target.tagName,
        name: e.target.name,
        className: e.target.className,
        value: e.target.value,
        matches_updates: e.target.matches('input[name="updates[]"]'),
        matches_quantity: e.target.matches('input[name="quantity"]'),
        isLocked: e.target.hasAttribute('data-locked')
      });
      
      // Handle both mini cart (updates[]) and product page (quantity) inputs
      if (e.target.matches('input[name="updates[]"]') || e.target.matches('input[name="quantity"]')) {
        // Check if input is locked
        const inputId = e.target.id || e.target.dataset.index;
        if (this.lockedInputs.has(inputId)) {

          return;
        }
        

        this.onQuantityChange(e);
      }
    });
    
    // Also listen for input events (fires during typing/button clicks)
    this.addEventListener('input', (e) => {
      if (e.target.matches('input[name="updates[]"]') || e.target.matches('input[name="quantity"]')) {

        
        // Check if input is locked to prevent flickering
        const inputId = e.target.id || e.target.dataset.index;
        if (this.lockedInputs.has(inputId)) {

          return;
        }
        
        // INSTANT PRICE UPDATE - Before any debouncing to prevent ANY flash
        const newQty = parseInt(e.target.value);
        if (!isNaN(newQty) && newQty >= 0) {
  
          this.updatePricesInstantly(newQty);
        }
        
        // Use minimal timeout to debounce rapid changes but keep it snappy
        clearTimeout(this.inputTimeout);
        this.inputTimeout = setTimeout(() => {
          this.onQuantityChange(e);
        }, 50); // Ultra-fast response for immediate feel
      }
    });
    
    // RE-ENABLED: Cart recommendations with better control
    this.watchCartRecommendations();
    
    // Clean up all prices on page load to remove .00 decimals
    this.removeDecimalsFromAllPrices();
    
    // Suppress quantity error messages for synced products
    this.suppressQuantityErrors();
    
    // Schedule initial sync after page load
    setTimeout(() => {
      this.syncQuantities();
    }, 1000);
  }

  onQuantityChange(event) {




    
    // Check if this input is locked to prevent flickering
    const input = event.target;
    const inputId = input.id || input.dataset.index;
    
    if (this.lockedInputs.has(inputId)) {

      return;
    }
    
    // Log ALL attributes of the input to see limits
    console.log('🔍 INPUT ATTRIBUTES:', {
      max: input.getAttribute('max'),
      min: input.getAttribute('min'),
      'data-max': input.dataset.max,
      'data-min': input.dataset.min,
      'data-cart-quantity': input.dataset.cartQuantity,
      step: input.getAttribute('step'),
      'data-quantity-variant-id': input.dataset.quantityVariantId,
      'all-attributes': Array.from(input.attributes).map(attr => `${attr.name}="${attr.value}"`).join(', ')
    });
    
    // Extra safety check: don't process if we're on main cart page
    const isMainCartPage = document.body.classList.contains('template-cart');
    const isInMiniCart = this.closest('mini-cart') || this.closest('.mini-cart') || this.closest('form.mini-cart');
    
    console.log('onQuantityChange context check:', {
      isMainCartPage,
      isInMiniCart: !!isInMiniCart,
      eventTarget: event.target.tagName + (event.target.className ? '.' + event.target.className : ''),
      inputName: event.target.name
    });
    
    if (isMainCartPage && !isInMiniCart) {

      return;
    }
    
    if (this.isUpdating) {

      return;
    }
    
    // CRITICAL: Prevent ALL other systems from handling this change

    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();
    
    // Set a global flag to block external interference
    window.SYNCHRONIZED_PRODUCTS_UPDATING = true;
    
    // DON'T hijack fetch - it blocks our own API calls too!
    // Instead, rely on the global flag and event blocking
    
    setTimeout(() => {
      window.SYNCHRONIZED_PRODUCTS_UPDATING = false;

      
      // Force a final price update after unblocking to ensure correct prices
      this.updateVisiblePrices();
    }, 5000); // Clear flag after 5 seconds
    
    const quantityInput = event.target;
    if (!quantityInput.matches('[name="updates[]"]')) {

      return;
    }
    
    const cartItem = quantityInput.closest('[data-handle]');
    if (!cartItem) {

      return;
    }
    
    const productHandle = cartItem.dataset.handle;

    
    // Only sync if this is one of our target products
    if (this.syncedProductHandles.includes(productHandle)) {
      const newQty = parseInt(quantityInput.value);
      
      
      
      // Reset update attempts counter for user-initiated changes
      this.updateAttempts = 0;
      this.syncQuantities(productHandle, newQty);
    } else {
      
    }
  }

  onCartUpdate(event) {

    console.log('🔍 EVENT DETAILS:', {
      event: event,
      hasEvent: !!event,
      eventSource: event ? event.source : 'NO EVENT',
      eventType: typeof event,
      eventKeys: event ? Object.keys(event) : 'NO KEYS'
    });

    
    // CRITICAL: Block cart-items events IMMEDIATELY to prevent external systems from interfering
    if (event && event.source === 'cart-items') {
      console.log('🚫 BLOCKING CART-ITEMS EVENT: External system trying to interfere', {
        source: event.source,
        isUpdating: this.isUpdating,
        syncInProgress: this.syncInProgress,
        fullEvent: event
      });
      return;
    }
    
    // Check if we have a pending dependent removal to execute
    if (this.pendingDependentRemoval && !this.isAutomatedDeletion) {
      
      const dependentHandle = this.pendingDependentRemoval;
      this.pendingDependentRemoval = null; // Clear the pending removal
      
      // Small delay to ensure the master product removal has completed
      setTimeout(() => {
        this.removeDependentProduct(dependentHandle);
      }, 200);
      
      return;
    }
    
        // Monitor cart transitions for recommendation reloading
    const currentItemCount = document.querySelectorAll('[data-handle]').length;

    
    // Skip recommendation fixes during automated deletion process
    if (this.isAutomatedDeletion) {

      return;
    }
    
    // If cart just got items and recommendations exist, check if they need refreshing
    if (currentItemCount > 0) {
      const recommendations = document.querySelector('cart-recommendations');
      if (recommendations) {
        const hasValidContent = this.hasValidRecommendationContent(recommendations);

        
        // Only fix recommendations if it's been stable for a bit (avoid deletion transition issues)
        if (!hasValidContent) {
          const now = Date.now();
          if (now - this.lastRecommendationFix > 5000) { // Increased throttle to 5 seconds
      
            this.lastRecommendationFix = now;
                          setTimeout(() => {
                // Double-check that we still have items and still need fixing
                const stillHasItems = document.querySelectorAll('[data-handle]').length > 0;
                const currentRecommendations = document.querySelector('cart-recommendations');
                const stillNeedsFix = !currentRecommendations || !this.hasValidRecommendationContent(currentRecommendations);
                
                if (stillHasItems && stillNeedsFix && !this.isAutomatedDeletion) {
          
                  this.fixBrokenRecommendations();
                } else {
        
                }
              }, 1000); // Longer delay for stability
          } else {

          }
        }
      }
    }
    
    // Circuit breaker: detect infinite loops
    const now = Date.now();
    if (now - this.lastCartUpdateTime < 1000) {
      this.cartUpdateCount++;
    } else {
      this.cartUpdateCount = 1; // Reset counter if enough time has passed
    }
    this.lastCartUpdateTime = now;
    
    if (this.cartUpdateCount > 10) {

      this.isUpdating = false;
      this.updateAttempts = 0;
      this.cartUpdateCount = 0;
      return;
    }
    
    // CRITICAL: Ignore cart updates that we initiated ourselves
    if (event && event.source === 'synchronized-products') {

      return;
    }
    
    // CRITICAL: Ignore cart updates when we're in the middle of updating
    if (this.isUpdating) {

      return;
    }
    
    // Safety check: don't sync on main cart page
    const isMainCartPage = document.body.classList.contains('template-cart');
    const isInMiniCart = this.closest('mini-cart') || this.closest('.mini-cart') || this.closest('form.mini-cart');
    
    if (isMainCartPage && !isInMiniCart) {

      return;
    }
    
    // Production behavior: Disable cart update syncing to prevent loops
    // Only rely on direct quantity input changes for stability

    
    // BUT still check for and remove duplicates caused by external systems
    if (source === 'cart-items') {

      this.removeDuplicateProducts(cartData);
    }
    
    // Only respond to user-initiated cart changes, not our own
    // if (event && (event.source === 'cart-items' || event.source === 'product-form')) {
    //   console.log('📝 Cart updated by user action, will sync after delay');
    //   // Small delay to ensure DOM has updated
    //   setTimeout(() => {
    //     if (!this.isUpdating) { // Double-check we're not updating
    //       console.log('Running sync after user cart update');
    //       this.syncQuantities();
    //     }
    //   }, 200);
    // } else {
    //   console.log('🔇 Ignoring cart update from unknown or automated source');
    // }
  }

  onCartError(event) {

    // Reset updating flag and attempt counter on error to prevent infinite loops
    this.isUpdating = false;
    this.syncInProgress = false;
    this.updateAttempts = 0;

  }

  updateCartCount(newCount) {

    
    // Find and update cart count elements (multiple selectors for different themes)
    const countSelectors = [
      '.cart-count-bubble span[aria-hidden="true"]',  // Common theme pattern
      '.cart-count-bubble',                           // Direct bubble
      '[data-cart-count]',                            // Data attribute
      '.cart-icon .count',                            // Icon count
      '.header__cart .count',                         // Header cart count
      '.cart-link .count',                            // Cart link count
      '#cart-icon-bubble span[aria-hidden="true"]',  // Specific ID
      '.header__icon--cart .cart-count-bubble span[aria-hidden="true"]' // Header icon specific
    ];
    
    let updated = false;
    
    countSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (element) {

          element.textContent = newCount;
          updated = true;
          
          // Also update the parent bubble visibility
          const bubble = element.closest('.cart-count-bubble');
          if (bubble) {
            if (newCount === 0) {
              bubble.style.display = 'none';
            } else {
              bubble.style.display = '';
            }
          }
        }
      });
    });
    
    // Update cart icon accessibility text
    const accessibilitySelectors = [
      '.cart-count-bubble .visually-hidden',
      '[data-cart-count] .visually-hidden',
      '.header__icon--cart .visually-hidden'
    ];
    
    accessibilitySelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        if (element && element.textContent.includes('cart')) {
          element.textContent = `${newCount} items in cart`;
        }
      });
    });
    
    if (updated) {
            } else {
        // No cart count elements found to update
    }
  }

  fixBrokenRecommendations() {

    
    const recommendations = document.querySelector('cart-recommendations');
    if (!recommendations) {
      
      return;
    }
    
    const dataUrl = recommendations.getAttribute('data-url');
    if (!dataUrl) {
      
      return;
    }
    
    
    
    // Get the first product ID from current cart items
    const firstCartItem = document.querySelector('[data-handle]');
    let productId = null;
    
    if (firstCartItem) {
      // Try to extract product ID from the cart item data
      const variantId = firstCartItem.dataset.variantId;
      if (variantId) {
        // Fetch the current cart to get proper product ID
        fetch('/cart.js')
          .then(response => response.json())
          .then(cartData => {
            if (cartData.items && cartData.items.length > 0) {
              productId = cartData.items[0].product_id;
    
              this.loadRecommendationsWithProductId(recommendations, dataUrl, productId);
            }
          })
          .catch(error => {
  
            this.loadRecommendationsWithProductId(recommendations, dataUrl, null);
          });
      }
    } else {
      
      this.loadRecommendationsWithProductId(recommendations, dataUrl, null);
    }
  }

  loadRecommendationsWithProductId(recommendations, baseUrl, productId) {
    
    
    // Build URL with product ID and timestamp to bypass cache
    let url = baseUrl;
    if (productId) {
      url = url.replace(/product_id=[^&]*/, `product_id=${productId}`);
      if (!url.includes('product_id=')) {
        url += url.includes('?') ? '&' : '?';
        url += `product_id=${productId}`;
      }
    }
    
    // Add timestamp to bypass cache
    url += url.includes('?') ? '&' : '?';
    url += `t=${Date.now()}`;
    

    
    // Show loading state
    recommendations.innerHTML = '<div class="loading">Loading recommendations...</div>';
    
    fetch(url)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newRecommendations = doc.querySelector('cart-recommendations');
        
        if (newRecommendations && newRecommendations.innerHTML.trim()) {
          recommendations.innerHTML = newRecommendations.innerHTML;
          
          
          // Remove decimals from new recommendation prices
          setTimeout(() => {
            this.removeDecimalsFromAllPrices();
          }, 100);
        } else {
          
          recommendations.innerHTML = '';
        }
      })
      .catch(error => {

        recommendations.innerHTML = '';
      });
  }

  setupCartCloseMonitoring() {

    
    // Monitor for cart drawer close button clicks
    document.addEventListener('click', (e) => {
      // Check for various cart close button selectors
      if (e.target.closest('drawer-close-button') || 
          e.target.closest('.cart-drawer__close') ||
          e.target.closest('[aria-label*="Close"]') ||
          e.target.closest('.header__icon--summary') ||
          e.target.matches('summary.header__icon') ||
          (e.target.closest('summary') && e.target.closest('.cart-drawer-container'))) {
        

        this.handleCartClose();
      }
    });
    
    // Monitor for ESC key press to close cart
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const cartDrawer = document.querySelector('cart-drawer details[open]');
        if (cartDrawer) {

          this.handleCartClose();
        }
      }
    });
    
    // CRITICAL: Listen for native Shopify cart updates and re-apply our pricing
    document.addEventListener('cart:updated', (event) => {
      // Only re-apply if this wasn't triggered by our own system
      if (event.detail && event.detail.source !== 'synchronized-products') {
        setTimeout(() => {
          this.updateVisiblePrices();
        }, 200); // Short delay to let section updates complete
      }
    });
    
    // Monitor for cart drawer details element changes
    const cartDrawer = document.querySelector('cart-drawer details');
    if (cartDrawer) {
      const closeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
            if (!cartDrawer.hasAttribute('open')) {

              setTimeout(() => {
                this.handleCartClose();
              }, 100); // Small delay to let close animation complete
            } else {

              
              // CRITICAL: Update prices immediately when cart opens if we have synced products
              setTimeout(() => {
                const cartItems = document.querySelectorAll('[data-handle]');
                const hasSyncedProducts = Array.from(cartItems).some(item => 
                  this.syncedProductHandles.includes(item?.dataset?.handle)
                );
                
                if (hasSyncedProducts) {

                  this.updateVisiblePrices();
                }
              }, 100); // Very early update
              
              setTimeout(() => {
                this.handleCartOpen();
              }, 300); // Delay to let cart content load
            }
          }
        });
      });
      
      closeObserver.observe(cartDrawer, { attributes: true });
      this.cartCloseObserver = closeObserver; // Store for cleanup
    }
  }

  handleCartClose() {
    const now = Date.now();
    if (now - this.lastCartClose < 1000) { // Throttle to max 1 close handler per second

      return;
    }
    this.lastCartClose = now;
    

    
    // Store current cart state for later restoration
    setTimeout(() => {
      // Check if cart has items for future recommendation loading
      fetch('/cart.js')
        .then(response => response.json())
        .then(cartData => {
          const itemCount = cartData.item_count || 0;

          
          // If cart has items, store the first product ID for next time cart opens
          if (itemCount > 0 && cartData.items && cartData.items.length > 0) {
            this.lastCartProductId = cartData.items[0].product_id;

            
            // Prepare recommendations for next cart open
            this.prepareRecommendationsForNextOpen();
          } else {
  
            this.lastCartProductId = null;
          }
        })
        .catch(error => {

        });
    }, 200); // Delay to ensure cart close has completed
  }

  prepareRecommendationsForNextOpen() {
    
    
    // Find recommendations element and reset its state if needed
    setTimeout(() => {
      const recommendations = document.querySelector('cart-recommendations');
      if (recommendations && this.lastCartProductId) {
        const dataUrl = recommendations.getAttribute('data-url');
        if (dataUrl) {
          // Update the data-url to include the correct product ID for next time
          let newUrl = dataUrl.replace(/product_id=[^&]*/, `product_id=${this.lastCartProductId}`);
          if (!newUrl.includes('product_id=')) {
            newUrl += newUrl.includes('?') ? '&' : '?';
            newUrl += `product_id=${this.lastCartProductId}`;
          }
          
          recommendations.setAttribute('data-url', newUrl);

        }
      }
    }, 500);
  }

  handleCartOpen() {
    const now = Date.now();
    if (now - this.lastCartOpen < 1000) { // Throttle to max 1 open handler per second

      return;
    }
    this.lastCartOpen = now;
    

    
    // Wait a bit for cart content to fully load
    setTimeout(() => {
      const cartItems = document.querySelectorAll('[data-handle]');
      const recommendations = document.querySelector('cart-recommendations');
      

      
      // CRITICAL: Update line item prices on cart open if we have synchronized products
      const hasSyncedProducts = Array.from(cartItems).some(item => 
        this.syncedProductHandles.includes(item.dataset.handle)
      );
      
      if (hasSyncedProducts) {

        this.updateVisiblePrices();
      }
      
      if (cartItems.length > 0 && recommendations) {
        // Better verification - check for actual product recommendations
        const hasValidContent = this.hasValidRecommendationContent(recommendations);
        

        
        if (!hasValidContent) {
  
          
          // Use a longer delay to avoid conflicts with cart opening animations
          if (now - this.lastRecommendationFix > 2000) { // Shorter throttle for cart open fix
            this.lastRecommendationFix = now;
            setTimeout(() => {
              this.fixBrokenRecommendations();
            }, 800);
          } else {

          }
        } else {
  
        }
      } else if (cartItems.length === 0) {
        
      }
    }, 600); // Longer delay to ensure cart is fully rendered
  }

  hasValidRecommendationContent(recommendations) {
    if (!recommendations) {
      
      return false;
    }
    
    const innerHTML = recommendations.innerHTML?.trim();
    if (!innerHTML) {
      
      return false;
    }
    
    // Check for loading states
    if (recommendations.querySelector('.loading, .spinner, .loading-overlay')) {

      return false;
    }
    
    // Check for actual product recommendations (expanded selectors)
    const productRecommendations = recommendations.querySelectorAll(
      '.product-container, .card-product, [data-product-id], .product-item, .recommendation-item, .product, .product-card'
    );

    
    if (productRecommendations.length === 0) {
      
      
      return false;
    }
    
    // Check if recommendations have proper content (images, titles, prices)
    let validProducts = 0;
    productRecommendations.forEach((product, index) => {
      const hasTitle = product.querySelector('.product-title, .card-product__title, h3, h4, .product-name, .link, a[href*="/products/"]');
      const hasPrice = product.querySelector('.price, .money, price-money, .price-item');
      const hasImage = product.querySelector('img');
      const hasProductLink = product.querySelector('a[href*="/products/"]');
      
      if (hasTitle || hasPrice || hasProductLink) {
        validProducts++;
      }
      
      if (index < 3) { // Only log first 3 for brevity

      }
    });
    
    const isValid = validProducts > 0;
    
    
    return isValid;
  }

  syncQuantities(changedProductHandle = null, newQuantity = null) {

    
    // Safety check: don't sync on main cart page
    const isMainCartPage = document.body.classList.contains('template-cart');
    const isInMiniCart = this.closest('mini-cart') || this.closest('.mini-cart') || this.closest('form.mini-cart');
    
    if (isMainCartPage && !isInMiniCart) {

      return;
    }
    
    if (this.isUpdating && this.updateAttempts >= this.maxUpdateAttempts) {

      this.isUpdating = false;
      this.updateAttempts = 0;
      return;
    }
    
    if (this.isUpdating) {

      return;
    }
    
    // SPECIAL CASE: Handle quantity = 0 based on deletion behavior
    if (newQuantity === 0 && changedProductHandle) {
      const currentBehavior = this.getDeletionBehaviorFromAddon() || 'wearable_master';
      
      // For bidirectional deletion, quantity = 0 should trigger deletion of both products
      if (currentBehavior === 'bidirectional') {

        // Let the deletion logic handle this - don't return early
      }
      // For wearable_master: if smartvibes goes to 0, don't sync apollo-wearable (original behavior)
      else if (currentBehavior === 'wearable_master' && changedProductHandle === 'smartvibes') {

        return;
      }
      // For independent: let each product be set to 0 independently
      else if (currentBehavior === 'independent') {

        return;
      }
    }
    
    const cartItems = this.querySelectorAll('[data-handle]');

    
    const syncedItems = [];
    
    // Find all synced products in cart
    // CRITICAL: Group by discount status to handle multiple variants/colors correctly
    cartItems.forEach(item => {
      const handle = item.dataset.handle;

      
      if (this.syncedProductHandles.includes(handle)) {
        const quantityInput = item.querySelector('[name="updates[]"]');
        if (quantityInput) {
          // Check if this item has a discount (selling plan)
          const variantId = quantityInput.dataset.quantityVariantId || quantityInput.getAttribute('data-quantity-variant-id');
          const lineIndex = quantityInput.dataset.index || quantityInput.getAttribute('data-index');
          
          // Check for quantity limits from multiple sources
          const maxQuantity = quantityInput.getAttribute('max') || 
                              quantityInput.dataset.max ||
                              quantityInput.getAttribute('data-max');
          const minQuantity = quantityInput.getAttribute('min') || 
                              quantityInput.dataset.min || 
                              quantityInput.getAttribute('data-min') || 0;
          
          syncedItems.push({
            handle: handle,
            element: item,
            input: quantityInput,
            quantity: parseInt(quantityInput.value),
            variantId: variantId,
            index: lineIndex,
            maxQuantity: maxQuantity ? parseInt(maxQuantity) : null,
            minQuantity: parseInt(minQuantity)
          });
          console.log('Added synced item:', handle, {
            quantity: parseInt(quantityInput.value),
            variantId: variantId,
            lineIndex: lineIndex,
            maxQuantity: maxQuantity ? parseInt(maxQuantity) : null,
            minQuantity: parseInt(minQuantity),
            inputAttributes: {
              max: quantityInput.getAttribute('max'),
              'data-max': quantityInput.dataset.max,
              min: quantityInput.getAttribute('min'),
              'data-min': quantityInput.dataset.min
            }
          });
        }
      }
    });
    

    
    // CRITICAL: Only sync if we have EXACTLY one of each product type
    // Skip syncing if there are multiple variants/colors (multiple wearables or smartvibes)
    const apolloItems = syncedItems.filter(i => i.handle === 'apollo-wearable');
    const smartvibesItems = syncedItems.filter(i => i.handle === 'smartvibes');
    
    // CRITICAL: Re-enable syncing for paired items
    // Pair-based deletion is handled separately in handleProductDeletion
    if (apolloItems.length > 1 || smartvibesItems.length > 1) {
      console.log('🚫 SKIPPING SYNC: Multiple variants detected', {
        apolloCount: apolloItems.length,
        smartvibesCount: smartvibesItems.length
      });

      return;
    }
    
    // If we have both products in cart (one of each), sync their quantities
    if (apolloItems.length === 1 && smartvibesItems.length === 1) {

      
      const firstItem = apolloItems[0];
      const secondItem = smartvibesItems[0];
      const firstHandle = firstItem.handle;
      const secondHandle = secondItem.handle;
      
      let targetQuantity;
      
      if (changedProductHandle && newQuantity !== null) {
        // User directly changed a quantity - sync both products to this exact quantity
        targetQuantity = newQuantity;
  
        
        // Special handling for decreases - if user is decreasing, respect that intent
        if (newQuantity === 0) {
  
        }
      } else {
        // Fallback when we don't know which product changed (e.g., cart updates)
        // Use the maximum quantity to avoid reducing items unintentionally
        targetQuantity = Math.max(firstItem.quantity, secondItem.quantity);

      }
      
      // Check if target quantity would violate any limits
      const firstMax = firstItem.maxQuantity;
      const secondMax = secondItem.maxQuantity;
      
      console.log('🔍 LIMIT ANALYSIS:', {
        targetQuantity,
        firstProduct: firstHandle,
        firstMax: firstMax || 'no limit',
        firstCurrent: firstItem.quantity,
        secondProduct: secondHandle, 
        secondMax: secondMax || 'no limit',
        secondCurrent: secondItem.quantity
      });
      
      // Find the most restrictive limit
      let effectiveMaxLimit = null;
      if (firstMax && secondMax) {
        effectiveMaxLimit = Math.min(firstMax, secondMax);

      } else if (firstMax) {
        effectiveMaxLimit = firstMax;

      } else if (secondMax) {
        effectiveMaxLimit = secondMax;

      }
      
             // DISABLED: Limit adjustment since we're bypassing all quantity limits
       // if (effectiveMaxLimit && targetQuantity > effectiveMaxLimit) {
       //   console.log('🚫 TARGET QUANTITY', targetQuantity, 'EXCEEDS EFFECTIVE LIMIT', effectiveMaxLimit, '- ADJUSTING');
       //   
       //   // If user was trying to increase but hit a limit, show warning
       //   if (changedProductHandle && newQuantity > effectiveMaxLimit) {
       //     console.warn('⚠️ Cannot sync to quantity', newQuantity, 'due to product limits. Maximum possible:', effectiveMaxLimit);
       //     // You could show a user notification here if needed
       //   }
       //   
       //   targetQuantity = effectiveMaxLimit;
       // }
       
       
       
       
       
       // Check if the two products have incompatible limits
       if (firstMax && secondMax && firstMax !== secondMax) {
         console.warn('⚠️ PRODUCTS HAVE INCOMPATIBLE LIMITS:', {
           [firstHandle]: firstMax,
           [secondHandle]: secondMax,
           'synchronized_to': effectiveMaxLimit
         });
       }
      
      // Check if target quantity exceeds any product's maximum
      const firstMaxQty = firstItem.maxQuantity;
      const secondMaxQty = secondItem.maxQuantity;
      
      // REMOVED: No longer restrict quantities based on product limits
      // if (firstMaxQty && targetQuantity > firstMaxQty) {
      //   console.log(`Target quantity ${targetQuantity} exceeds ${firstHandle} max of ${firstMaxQty}`);
      //   targetQuantity = firstMaxQty;
      // }
      // 
      // if (secondMaxQty && targetQuantity > secondMaxQty) {
      //   console.log(`Target quantity ${targetQuantity} exceeds ${secondHandle} max of ${secondMaxQty}`);
      //   targetQuantity = secondMaxQty;
      // }
      

      
      // Update quantities if they don't match
      this.isUpdating = true;
      this.syncInProgress = true; // Flag to prevent cart recommendations interference
      this.updateAttempts++;

      
      let needsUpdate = false;
      const updatePromises = [];
      
      if (firstItem.quantity !== targetQuantity) {

        updatePromises.push(this.updateProductQuantity(firstItem, targetQuantity));
        needsUpdate = true;
      } else {

      }
      
      if (secondItem.quantity !== targetQuantity) {

        updatePromises.push(this.updateProductQuantity(secondItem, targetQuantity));
        needsUpdate = true;
      } else {
        
      }
      
      if (needsUpdate && updatePromises.length > 0) {
        // Lock all synced inputs to prevent flickering during updates
        this.lockSyncedInputs();
        
        // Wait for all updates to complete
        Promise.allSettled(updatePromises).then(results => {

          
          this.isUpdating = false;
          this.updateAttempts = 0;

          
          // CRITICAL: Check for duplicates after sync completes (external systems might add duplicates)
          setTimeout(() => {

            fetch('/cart.js')
              .then(response => response.json())
              .then(cartData => {
                if (cartData.items && cartData.items.length > 2) {

                  this.removeDuplicateProducts(cartData);
                } else {

                }
              })
              .catch(error => console.log('Post-sync duplicate check error:', error));
          }, 500);
          
          // Check results - bypassed limits are treated as successes
          const failures = results.filter(result => {
            if (result.status === 'rejected') return true;
            if (result.status === 'fulfilled' && result.value?.limited && !result.value?.bypassed) return true;
            return false;
          });
          
          const bypassed = results.filter(result => 
            result.status === 'fulfilled' && result.value?.bypassed
          );
          
          if (bypassed.length > 0) {
  
          }
          
          if (failures.length > 0) {
  
          }
          
                    // INSTANT UI updates - no delays for maximum speed
                      this.forceUISync();
            this.updateVisiblePrices();
            
            // Set up price monitoring to prevent external overwrites
            this.setupPriceMonitoring();
            
            this.unlockSyncedInputs();
            this.syncInProgress = false; // Clear sync flag
            
            // Suppress any quantity error messages that might have appeared
            setTimeout(() => {
              this.suppressQuantityErrors();
            }, 100);
            
    
    
        });
      } else {
        // No updates needed, reset flags immediately
        this.isUpdating = false;
        this.syncInProgress = false;
        this.updateAttempts = 0;

      }
    } else if (apolloItems.length === 1 || smartvibesItems.length === 1) {

    } else {

    }
  }

  getSectionsToRender() {
    // Get sections similar to how CartItems does it
    const sections = [
      {
        section: 'mini-cart'
      },
      {
        section: 'cart-icon-bubble'
      }
    ];

    // Add cart drawer section if it exists
    if (document.getElementById('cart-drawer')) {
      sections.push({
        section: 'cart-drawer'
      });
    }

    // Add free shipping section if it exists
    if (document.querySelector('.free-shipping')) {
      sections.push({
        section: 'free-shipping'
      });
    }

    return sections;
  }

      updateProductQuantity(item, quantity) {
    // Skip update if the item already has the target quantity
    if (item.quantity === quantity) {
      return Promise.resolve();
    }
    
    // DISABLED: Pre-limit checking since we're bypassing all quantity limits
    // if (item.maxQuantity && quantity > item.maxQuantity) {
    //   console.log('🚫 WOULD EXCEED DETECTED LIMIT:', {
    //     product: item.element.dataset.handle,
    //     targetQty: quantity,
    //     maxAllowed: item.maxQuantity
    //   });
    //   return Promise.resolve({ limited: true, message: `Would exceed detected limit of ${item.maxQuantity}` });
    // }
    
    // CRITICAL: Get the item key from the cart item element to ensure we update the exact item
    // This preserves pairing properties and selling plans
    const itemKey = item.element.dataset.itemKey;
    
    if (!itemKey) {

      return Promise.reject('No item key found');
    }
    
    // Use Shopify's cart update API with item key to preserve all properties including pairing
    const updates = {};
    updates[itemKey] = quantity;
    
    const updateData = {
      updates: updates,
      sections: this.getSectionsToRender().map(section => section.section),
      sections_url: window.location.pathname
    };
    
    console.log('🚀 MAKING FETCH REQUEST (key-based):', {
      url: '/cart/update.js',
      method: 'POST',
      body: updateData,
      productHandle: item.element.dataset.handle,
      currentQuantity: item.quantity,
      targetQuantity: quantity,
      itemKey: itemKey,
      elementDataHandle: item.element.dataset.handle,
      elementDataVariantId: item.element.dataset.variantId
    });
    
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(updateData)
    })
    .then(response => {
      console.log('📥 FETCH RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        return response.text().then(errorText => {
          
          
          // Check if it's a quantity limit error
          try {
            const errorJson = JSON.parse(errorText);
            
            if (errorJson.description && errorJson.description.toLowerCase().includes('can only add')) {

              // Return success instead of error to bypass the limit
              return { 
                limited: true, 
                bypassed: true,
                message: errorJson.description, 
                product: item.element.dataset.handle 
              };
            }
          } catch (e) {
            // Not JSON, check raw text
            if (errorText.toLowerCase().includes('can only add')) {

              // Return success instead of error to bypass the limit
              return { 
                limited: true, 
                bypassed: true,
                message: errorText, 
                product: item.element.dataset.handle 
              };
            }
          }
          // For other errors, still throw
          
          throw new Error(`${item.element.dataset.handle}: ${errorText}`);
        });
      }
      // /cart/change.js returns text, not JSON directly
      return response.text();
    })
    .then(responseText => {

      
      let result;
      try {
        // Parse the response text as JSON
        result = JSON.parse(responseText);

      } catch (parseError) {


        throw new Error('Failed to parse cart response as JSON');
      }
      
      console.log('📊 PARSED CART RESPONSE:', {
        itemCount: result.item_count,
        totalItems: result.items ? result.items.length : 'N/A',
        items: result.items ? result.items.map(item => ({
          handle: item.handle,
          variantId: item.variant_id,
          quantity: item.quantity,
          sellingPlan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : 'None',
          lineIndex: result.items.indexOf(item) + 1
        })) : 'N/A'
      });
      
      return result;
    })
    .then(result => {
      if (result.limited) {
        if (result.bypassed) {
          
          // Treat bypassed limits as successful updates - SIMPLIFIED
          item.input.value = quantity;

          
          // Trigger a change event to update UI elements
          item.input.dispatchEvent(new Event('change', { bubbles: true }));

        } else {

          // Reset updating flag if we hit a limit to prevent getting stuck
          this.isUpdating = false;
          this.syncInProgress = false;
          this.updateAttempts = 0;

        }
        
        return result;
      } else {

        
        // Update the input value to reflect the change - SIMPLIFIED
        const oldValue = item.input.value;
        item.input.value = quantity;

        
        // Trigger input change event for immediate UI updates
        item.input.dispatchEvent(new Event('change', { bubbles: true }));
        item.input.dispatchEvent(new Event('input', { bubbles: true }));

        
        // Trigger cart update events (but mark as our source to avoid loops)
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: JSON.stringify(result) }
        }));
        
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { 
            source: 'synchronized-products',
            cartData: result 
          });
        }
        
        // CRITICAL: Check for and remove duplicate products caused by external systems
        this.removeDuplicateProducts(result);
        
        // ADDITIONAL SAFETY: Schedule another duplicate check after external systems might interfere
        setTimeout(() => {

          fetch('/cart.js')
            .then(response => response.json())
            .then(cartData => {
              if (cartData.items && cartData.items.length > 2) {

                this.removeDuplicateProducts(cartData);
              }
            })
            .catch(error => console.log('Duplicate check fetch error:', error));
        }, 1000);
        
        // CRITICAL: Re-apply our discounted pricing after Shopify's section updates complete
        // This ensures our quantity-based pricing survives the section re-render
        setTimeout(() => {
          this.updatePricesWithDisplayedQuantities(result);
        }, 500); // Wait for section updates to complete
        
        // Additional safety net - re-apply pricing if it gets wiped out
        setTimeout(() => {
          this.updatePricesWithDisplayedQuantities(result);
        }, 1000);
      }
    })
    .catch(error => {
      console.error('🚨 SYNCHRONIZED PRODUCTS: Fetch error caught!', {
        error: error,
        message: error.message,
        stack: error.stack
      });

      // Reset updating flag on any error to prevent getting stuck  
      this.isUpdating = false;
      this.syncInProgress = false;
      this.updateAttempts = 0;

      
      // Return a bypassed result instead of throwing
      return { 
        limited: true, 
        bypassed: true, 
        message: error.message || error.toString(), 
        product: item.element.dataset.handle 
      };
    });
    
    // SKIP cart recommendations refresh during sync to prevent quantity flickering
    // setTimeout(() => {
    //   this.reinitializeCartRecommendations();
    // }, 1000);
  }

  removeDuplicateProducts(cartData) {
    if (!cartData || !cartData.items) return;
    
    console.log('🔍 CHECKING FOR DUPLICATES:', {
      totalItems: cartData.items.length,
      items: cartData.items.map(item => ({
        handle: item.handle,
        variantId: item.variant_id,
        quantity: item.quantity,
        hasSellingPlan: !!item.selling_plan_allocation,
        sellingPlanName: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : 'None'
      }))
    });
    
    // Look for Apollo Wearable duplicates (one with discount, one without)
    const apolloItems = cartData.items.filter(item => item.handle === 'apollo-wearable');
    
    if (apolloItems.length > 1) {
      console.log('🚨 DUPLICATE APOLLO WEARABLE DETECTED!', {
        count: apolloItems.length,
        items: apolloItems.map((item, index) => ({
          index: index + 1,
          variantId: item.variant_id,
          quantity: item.quantity,
          price: item.price,
          originalPrice: item.original_price,
          hasDiscount: item.price !== item.original_price,
          hasSellingPlan: !!item.selling_plan_allocation,
          sellingPlanName: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : 'None'
        }))
      });
      
      // Find the item with the discount (selling plan) and the one without
      const discountedItem = apolloItems.find(item => item.selling_plan_allocation);
      const nonDiscountedItem = apolloItems.find(item => !item.selling_plan_allocation);
      
      if (discountedItem && nonDiscountedItem) {

        
        // Calculate total quantity
        const totalQuantity = discountedItem.quantity + nonDiscountedItem.quantity;
        
        // Remove the non-discounted item by setting its quantity to 0
        const nonDiscountedLineIndex = cartData.items.indexOf(nonDiscountedItem) + 1;
        
        console.log('🗑️ REMOVING NON-DISCOUNTED ITEM:', {
          lineIndex: nonDiscountedLineIndex,
          quantity: nonDiscountedItem.quantity,
          price: nonDiscountedItem.price
        });
        
        // Remove the duplicate item
        fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            line: nonDiscountedLineIndex,
            quantity: 0
          })
        })
        .then(response => response.text())
        .then(responseText => {

          
          // Update the discounted item to have the total quantity
          const discountedLineIndex = cartData.items.indexOf(discountedItem) + 1;
          
          console.log('📈 UPDATING DISCOUNTED ITEM QUANTITY:', {
            lineIndex: discountedLineIndex,
            newQuantity: totalQuantity
          });
          
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              line: discountedLineIndex,
              quantity: totalQuantity
            })
          });
        })
        .then(response => response.text())
        .then(responseText => {

          
          // Refresh the cart display
          setTimeout(() => {
            const miniCart = document.querySelector('mini-cart');
            if (miniCart && typeof miniCart.renderContents === 'function') {
              // Fetch fresh cart data
              fetch('/cart.js')
                .then(response => response.json())
                .then(freshCartData => {

                  miniCart.renderContents({ 
                    ...freshCartData,
                    sections: {} // Let it fetch fresh sections
                  });
                });
            }
          }, 100);
        })
        .catch(error => {

        });
      }
    }
  }

  refreshCartSections(skipRecommendations = false) {

    
    // Try multiple approaches to refresh the cart display
    
    // Method 1: Use the theme's cart drawer refresh mechanism
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.onCartRefresh === 'function') {

      cartDrawer.onCartRefresh({ detail: { open: false } });
      return;
    }
    
    // Method 2: Get fresh cart data and use mini-cart's renderContents

    fetch('/cart.js', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(cartData => {

      
      // Get the mini-cart element
      const miniCart = document.querySelector('mini-cart');
      if (miniCart) {
        // Get the sections it needs
        let sections = miniCart.getSectionsToRender ? 
                      miniCart.getSectionsToRender().map(section => section.id) : 
                      ['mini-cart', 'cart-icon-bubble'];
        
        // Filter out cart-recommendations if skipRecommendations is true
        if (skipRecommendations) {
          sections = sections.filter(section => !section.includes('recommendations'));

        }
        

        
        // Fetch the sections with the cart data
        const sectionsParam = sections.join(',');
        return fetch(`/?sections=${sectionsParam}`)
          .then(response => response.json())
          .then(sectionsData => {

            
            // Use mini-cart's renderContents method with fresh data
            const cartDataWithSections = {
              ...cartData,
              sections: sectionsData
            };
            
            if (typeof miniCart.renderContents === 'function') {

              miniCart.renderContents(cartDataWithSections);
              
              // DISABLED: Skip delayed price updates to prevent flashing
              // setTimeout(() => {
              //   console.log('🧮 Updating prices after cart section refresh');
              //   this.updateVisiblePrices();
              // }, 100);
            } else {

              this.updateSectionsManually(sectionsData);
              
                              // DISABLED: Skip delayed price updates to prevent flashing
                // setTimeout(() => {
                //   console.log('🧮 Updating prices after manual section update');
                //   this.updateVisiblePrices();
                // }, 100);
            }
          });
      } else {

        return this.updateSectionsManually({});
      }
    })
    .catch(error => {

      
      // Method 3: Fallback - trigger cart refresh event

      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { open: false }
      }));
      
      // Method 4: Last resort - reload the page after a delay
      setTimeout(() => {

        window.location.reload();
      }, 2000);
    });
  }

  setupPriceMonitoring() {
    // Stop any existing monitoring
    if (this.priceMonitorInterval) {
      clearInterval(this.priceMonitorInterval);
    }
    

    
    // Store the correct cart total
    fetch('/cart.js')
      .then(res => res.json())
      .then(cartData => {
        const correctTotal = cartData.total_price;
        const correctTotalFormatted = Math.round(correctTotal / 100);
        

        
        // Monitor price elements every 1 second for 5 seconds (reduced to minimize flashing)
        let monitorCount = 0;
        this.priceMonitorInterval = setInterval(() => {
          monitorCount++;
          
          // Only monitor BDI elements to avoid breaking structure
          const subtotalElements = document.querySelectorAll('#mini-cart-subtotal bdi, .subtotal .price bdi, .cart-total .price bdi, button[name="checkout"] .price bdi');
          
          subtotalElements.forEach((el, index) => {
            const currentText = el.textContent.trim();
            const currentPrice = currentText.replace(/[^0-9]/g, '');
            
            // Check if price has been incorrectly changed
            if (currentPrice && parseInt(currentPrice) !== correctTotalFormatted && parseInt(currentPrice) !== correctTotalFormatted * 100) {

              
              // Only update BDI text content
              el.textContent = `$${correctTotalFormatted}.00 USD`;
            }
          });
          
          // Stop monitoring after 5 seconds (5 checks)
          if (monitorCount >= 5) {
            clearInterval(this.priceMonitorInterval);

          }
        }, 1000); // Check every 1 second instead of 500ms
      })
      .catch(error => {

      });
  }

  updateVisiblePrices() {
    if (this.priceUpdateInProgress) {

      return;
    }
    
    this.priceUpdateInProgress = true;

    
    // Get current cart data to calculate new prices
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
    .then(response => response.json())
    .then(cartData => {

      
      // Since we might have bypassed quantity limits, use displayed quantities instead of server quantities
      this.updatePricesWithDisplayedQuantities(cartData);
      
      // Single delayed update to catch section renders (reduced from 3 to prevent flashing)
      setTimeout(() => {

        this.updatePricesWithDisplayedQuantities(cartData);
        
        // Set up price monitoring AFTER the delayed update to reduce flashing
        this.setupPriceMonitoring();
      }, 600); // Single update at optimal timing
    })
    .catch(error => {

      this.priceUpdateInProgress = false;
    });
  }

  updatePricesWithDisplayedQuantities(cartData) {
    
    // Safety check: ensure cartData has items
    if (!cartData || !cartData.items) {

      this.priceUpdateInProgress = false;
      return;
    }
    

    
    // Update individual line item prices - OPTIMIZED for speed
    cartData.items.forEach((item, itemIndex) => {
        const productHandle = item.handle;
        

        
        // Only update our synchronized products - quick check
        if (!this.syncedProductHandles.includes(productHandle)) {

          return;
        }
          
        // CRITICAL: Find the correct cart item by unique item key
        // This ensures we update the right line item even when there are multiple items with the same variant ID
        const cartItem = document.querySelector(`[data-item-key="${item.key}"]`);
          
        if (!cartItem) {

          return;
        }
        
        // CRITICAL: Always use item.quantity from cart data, not displayed input value
        // The displayed input might be for a different line item when there are multiple variants
        const actualQuantity = item.quantity;
        
        // Check if this is a discounted item
        const isDiscounted = item.original_price !== item.final_price;
        const originalLineTotal = item.original_price * actualQuantity;
        const finalLineTotal = item.final_price * actualQuantity;
        const formattedOriginalPrice = Math.round(originalLineTotal / 100);
        const formattedFinalPrice = Math.round(finalLineTotal / 100);
        


        
        // Fast price element update - handle discounted pricing structure
        const priceElements = cartItem.querySelectorAll('price-money bdi, .price bdi');
        

        
        priceElements.forEach((priceEl, priceIndex) => {
          // Skip SmartVibes protected pricing elements
          const priceMoneyEl = priceEl.closest('price-money');
          if (priceMoneyEl && (priceMoneyEl.hasAttribute('data-smartvibes-original') || priceMoneyEl.hasAttribute('data-smartvibes-current'))) {

            return; // Skip SmartVibes protected elements
          }
          
          const currentText = priceEl.textContent.trim();
          
          // Quick price validation and update
          if (currentText.includes('$') && !currentText.includes('$0.') && !currentText.includes('/')) {
            let newPrice = formattedFinalPrice; // Default to final price
            
            // For discounted items, determine which price to use
            if (isDiscounted) {
              // Check if this is the original (crossed-out) price element
              const isOriginalPrice = priceEl.closest('s') || 
                                    priceEl.closest('.price--on-sale') ||
                                    priceEl.closest('.price-item--regular') ||
                                    priceEl.closest('[class*="regular"]') ||
                                    priceEl.closest('[class*="original"]');
              
              if (isOriginalPrice) {
                newPrice = formattedOriginalPrice; // Use original price for crossed-out elements
              } else {
                newPrice = formattedFinalPrice; // Use final price for sale elements
              }
            }
            
            // Update the price element
            if (priceEl.querySelector('.price__prefix')) {
              const prefix = priceEl.querySelector('.price__prefix');
              priceEl.innerHTML = prefix.outerHTML + newPrice;
            } else {
              priceEl.textContent = `$${newPrice}`;
            }
            

          } else {

          }
                });
    });
      
    // Hide USD currency codes after price updates
    if (typeof window.hideUSDCurrencyCodes === 'function') {
      setTimeout(window.hideUSDCurrencyCodes, 50);
    }
      
    // Fast cart total update
    const cartTotal = cartData.total_price;
    const formattedTotal = Math.round(cartTotal / 100);
    
    // Quick subtotal update - essential selectors only
    // Target only BDI elements to avoid breaking HTML structure
    const subtotalElements = document.querySelectorAll('#mini-cart-subtotal bdi, .subtotal .price bdi, .cart-total .price bdi, button[name="checkout"] .price bdi');

    subtotalElements.forEach((el, index) => {

      const oldPrice = el.textContent.trim();
      // Only update BDI text content to preserve structure
      el.textContent = `$${formattedTotal}.00 USD`;

    });
      
    
    this.priceUpdateInProgress = false;
  }

  removeDecimalsFromAllPrices() {

    
    // Find all price elements on the page
    const priceSelectors = [
      'price-money bdi',
      '.price bdi',
      '.money',
      '.totals__subtotal-value',
      '[class*="price"] bdi',
      '[data-price]'
    ];
    
    priceSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        const currentText = element.textContent.trim();
        
        // Check if it contains .00 and is a price (contains $ or currency)
        if (currentText.includes('.00') && (currentText.includes('$') || /[\d,]+\.00/.test(currentText))) {
          const cleanText = currentText.replace(/\.00/g, '');

          
          // Preserve any HTML structure if it exists
          if (element.querySelector('.price__prefix')) {
            const prefix = element.querySelector('.price__prefix');
            const numberPart = cleanText.replace(/\$/, '').replace(/,/g, '');
            element.innerHTML = prefix.outerHTML + numberPart;
          } else {
            element.textContent = cleanText;
          }
          
          // Mark as cleaned to avoid re-processing
          element.setAttribute('data-decimals-removed', 'true');
        }
      });
    });
    
  }

  suppressQuantityErrors() {
    // Hide any existing error messages for synced products
    this.syncedProductHandles.forEach(handle => {
      const cartItem = this.querySelector(`[data-handle="${handle}"]`);
      if (cartItem) {
        const errorElements = cartItem.querySelectorAll('.cart-item__error, .cart-item__error-text, [id*="Line-item-error"]');
        errorElements.forEach(errorEl => {
          errorEl.style.display = 'none';
          errorEl.innerHTML = '';
        });
      }
    });
  }

  getDeletionBehaviorFromAddon() {
    // Use the global setting from the synchronized-products element
    const behavior = this.getAttribute('data-deletion-behavior');

    return behavior || 'wearable_master';
  }

  handleProductDeletion(handle) {
    // Get current deletion behavior (refresh it in case it changed)
    const currentBehavior = this.getDeletionBehaviorFromAddon() || 'wearable_master';

    
    // CRITICAL: If deletion behavior is "none", completely skip deletion handling
    // This allows cart.js property-based paired deletion to work without interference
    if (currentBehavior === 'none') {

      return;
    }
    
    if (!this.syncedProductHandles.includes(handle)) {

      return;
    }
    
    // CRITICAL: Check if there are multiple variants in the cart
    // If so, skip bidirectional/dependent deletion to avoid deleting unrelated color variants
    const cartItems = this.querySelectorAll('[data-handle]');
    const apolloItems = Array.from(cartItems).filter(item => item.dataset.handle === 'apollo-wearable');
    const smartvibesItems = Array.from(cartItems).filter(item => item.dataset.handle === 'smartvibes');
    
    const hasMultipleVariants = apolloItems.length > 1 || smartvibesItems.length > 1;
    
    if (hasMultipleVariants) {


      // Don't return - let the normal cart delete proceed, just skip the bidirectional logic below
      return; // Exit early to skip all dependent deletion logic
    }
    
    // Check if we're already processing this specific product deletion to avoid double processing
    if (this.processingDeletions && this.processingDeletions.has(handle)) {

      
      // For bidirectional deletion, we still need to remove the OTHER product even if this one is already being processed
      if (currentBehavior === 'bidirectional') {
        const otherProduct = handle === 'apollo-wearable' ? 'smartvibes' : 'apollo-wearable';

        
        // Only proceed if we're NOT already processing the other product
        if (!this.processingDeletions.has(otherProduct)) {

          this.removeDependentProduct(otherProduct);
        } else {

        }
      }
      return;
    }
    
    switch (currentBehavior) {
      case 'wearable_master':
        if (handle === 'apollo-wearable') {
          // Store that we need to remove the dependent product
          this.pendingDependentRemoval = 'smartvibes';
          
          // Also add a backup timer in case cart events don't fire
          setTimeout(() => {
            if (this.pendingDependentRemoval) {
              const dependentHandle = this.pendingDependentRemoval;
              this.pendingDependentRemoval = null;
              this.removeDependentProduct(dependentHandle);
            }
          }, 1000);
        }
        // For smartvibes deletion, do nothing - let smartvibes be removed but keep apollo-wearable
        break;
        
      case 'bidirectional':
        const otherProduct = handle === 'apollo-wearable' ? 'smartvibes' : 'apollo-wearable';



        
        // Store that we need to remove the other product
        this.pendingDependentRemoval = otherProduct;
        
        // Also add a backup timer in case cart events don't fire
        setTimeout(() => {
          if (this.pendingDependentRemoval) {
            const dependentHandle = this.pendingDependentRemoval;
            this.pendingDependentRemoval = null;
            this.removeDependentProduct(dependentHandle);
          }
        }, 1000);
        break;
        
      case 'independent':

        // Do nothing - products can be deleted independently
        break;
        
      default:

        this.deletionBehavior = 'wearable_master';
        this.handleProductDeletion(handle);
        break;
    }
  }

  removeDependentProduct(dependentHandle) {

    
    // CRITICAL: Find ALL items with this handle (for multiple variants/colors)
    const dependentItems = this.querySelectorAll(`[data-handle="${dependentHandle}"]`);

    
    if (dependentItems.length === 0) {

      return;
    }
    
    // Collect all variant IDs to remove
    const variantIds = [];
    dependentItems.forEach((dependentItem, index) => {
      const variantId = dependentItem.dataset.variantId;
      if (variantId) {
        variantIds.push(variantId);

      }
    });
    
    if (variantIds.length === 0) {

      return;
    }
    
    // Set flag to prevent our event listener from processing any related events
    this.isAutomatedDeletion = true;
    
    // Build updates object to set ALL variant quantities to 0
    const updates = {};
    variantIds.forEach(variantId => {
      updates[variantId] = 0;
    });
    
    // Method 1: Direct cart API call to remove ALL instances

    fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        updates: updates  // Set ALL variant quantities to 0
      })
    })
    .then(response => response.json())
    .then(data => {

      
      // Reset the automated deletion flag
      setTimeout(() => {
        this.isAutomatedDeletion = false;

      }, 1000); // Longer delay to ensure cart is fully updated
      
      // Update cart count in header immediately
      this.updateCartCount(data.item_count || 0);
      
      // If cart is now empty, prepare to reinitialize recommendations
      if ((data.item_count || 0) === 0) {

        
        // CRITICAL: Force a complete cart refresh to ensure server state is clean
        setTimeout(() => {
          this.reinitializeCartRecommendations();
          
          // Double-check cart is truly empty by fetching fresh cart data
          fetch('/cart.js')
            .then(r => r.json())
            .then(freshCart => {

              if (freshCart.item_count > 0) {

                // Force clear all items
                const updates = {};
                freshCart.items.forEach(item => {
                  updates[item.variant_id] = 0;
                });
                return fetch('/cart/update.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ updates })
                });
              }
            })
            .catch(err => console.error('🔍 EMPTY CART VERIFICATION: Error:', err));
        }, 1000);
      }
      
      // Trigger cart update events to refresh UI
      document.dispatchEvent(new CustomEvent('cart:updated', { 
        detail: { cart: JSON.stringify(data) } 
      }));
      
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'automated-deletion', 
          cartData: data 
        });
      }
      
      // Force aggressive UI refresh since theme events aren't working
      
      // Method 1: Try multiple theme refresh methods
      setTimeout(() => {
        const cartDrawer = document.querySelector('mini-cart');
        if (cartDrawer) {
          // Get the sections needed for proper rendering
          const sections = cartDrawer.getSectionsToRender ? 
                          cartDrawer.getSectionsToRender().map(section => section.id) : 
                          ['mini-cart', 'cart-icon-bubble'];
          
          
          // Fetch fresh sections with current cart data
          const sectionsParam = sections.join(',');
          fetch(`/?sections=${sectionsParam}`)
            .then(response => response.json())
            .then(sectionsData => {
              
              // Create proper cart data with sections
              const cartDataWithSections = {
                ...data,
                sections: sectionsData
              };
              
              // Try theme's built-in refresh methods
              if (typeof cartDrawer.refresh === 'function') {
                cartDrawer.refresh();
              } else if (typeof cartDrawer.renderContents === 'function') {
                cartDrawer.renderContents(cartDataWithSections);
              } else {
                // Manual section update
                sections.forEach(sectionId => {
                  const element = document.getElementById(sectionId);
                  if (element && sectionsData[sectionId]) {
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = sectionsData[sectionId];
                    const sectionContent = tempDiv.querySelector('.shopify-section') || tempDiv.firstElementChild;
                    if (sectionContent) {
                      element.innerHTML = sectionContent.innerHTML;
                    }
                  }
                });
              }
            })
            .catch(error => {

              window.location.reload();
            });
        }
      }, 100);
      
             // Method 2: Backup - direct DOM manipulation to remove any remaining items
       setTimeout(() => {
         // Remove the specific product that should have been deleted
         const targetItem = document.querySelector(`[data-handle="${dependentHandle}"]`);
         if (targetItem) {
           targetItem.remove();
         }
         
         // Check if cart is now empty and show empty state
         const remainingItems = document.querySelectorAll('[data-handle]');
         const newItemCount = remainingItems.length;
         
         // Update cart count in header
         this.updateCartCount(newItemCount);
         
         if (newItemCount === 0) {
           const cartForm = document.querySelector('form.mini-cart');
           if (cartForm) {
             cartForm.classList.add('is-empty');
           }
           
           // Reinitialize cart recommendations when cart becomes empty
           setTimeout(() => {
             this.reinitializeCartRecommendations();
           }, 800);
         }
         
         // Final fallback: if items are still showing after all attempts, reload the page
         setTimeout(() => {
           const stillRemainingItems = document.querySelectorAll('[data-handle]');
           if (data.item_count === 0 && stillRemainingItems.length > 0) {
             window.location.reload();
           }
         }, 3000);
       }, 1500); // Longer delay to let the main refresh attempt complete first
    })
    .catch(error => {
      
      // Reset the automated deletion flag even on error
      setTimeout(() => {
        this.isAutomatedDeletion = false;
  
      }, 1000);
      
      // Fallback: Try the old quantity input method

      const quantityInput = dependentItem.querySelector('[name="updates[]"]');
      if (quantityInput) {
        quantityInput.value = '0';
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  updatePricesInstantly(newQuantity) {
    // Get current cart data to use actual prices instead of hardcoded values  
    fetch('/cart.js')
      .then(response => response.json())
      .then(cartData => {
        // Update each synced product with correct pricing
        cartData.items.forEach(item => {
          if (this.syncedProductHandles.includes(item.handle)) {
            const cartItem = document.querySelector(`[data-handle="${item.handle}"]`);
            if (!cartItem) return;
            
            // Use the same discounted pricing logic as the main update function
            const isDiscounted = item.original_price !== item.final_price;
            const originalLineTotal = item.original_price * newQuantity;
            const finalLineTotal = item.final_price * newQuantity;
            const formattedOriginalPrice = Math.round(originalLineTotal / 100);
            const formattedFinalPrice = Math.round(finalLineTotal / 100);
            
            // Update price elements in this cart item
            const priceElements = cartItem.querySelectorAll('price-money bdi, .price bdi');
            
            priceElements.forEach(priceEl => {
              // Skip SmartVibes protected pricing elements
              const priceMoneyEl = priceEl.closest('price-money');
              if (priceMoneyEl && (priceMoneyEl.hasAttribute('data-smartvibes-original') || priceMoneyEl.hasAttribute('data-smartvibes-current'))) {
                return;
              }
              
              const currentText = priceEl.textContent.trim();
              
              if (currentText.includes('$') && /\$\d/.test(currentText)) {
                let newPrice = formattedFinalPrice; // Default to final price
                
                // For discounted items, determine which price to use
                if (isDiscounted) {
                  const isOriginalPrice = priceEl.closest('s') || 
                                        priceEl.closest('.price--on-sale') ||
                                        priceEl.closest('.price-item--regular') ||
                                        priceEl.closest('[class*="regular"]') ||
                                        priceEl.closest('[class*="original"]');
                  
                  if (isOriginalPrice) {
                    newPrice = formattedOriginalPrice;
                  }
                }
                
                // Update the price element
                if (priceEl.querySelector('.price__prefix')) {
                  const prefix = priceEl.querySelector('.price__prefix');
                  priceEl.innerHTML = prefix.outerHTML + newPrice;
                } else {
                  priceEl.textContent = `$${newPrice}`;
                }
              }
            });
          }
        });
        
        // Hide USD currency codes after lightning price updates
        if (typeof window.hideUSDCurrencyCodes === 'function') {
          setTimeout(() => {
            window.hideUSDCurrencyCodes();
          }, 50);
        }
      })
      .catch(error => {

        // Fallback to the simpler logic if cart fetch fails
        this.updateVisiblePrices();
      });
  }

  updatePricesSynchronously(newQuantity) {

    
    const unitPrice = 149;
    const newTotalPrice = unitPrice * newQuantity;
    const totalCartPrice = newTotalPrice * 2;
    

    
    // SYNCHRONOUS DOM updates - everything happens in one go
    const allPriceElements = document.querySelectorAll('price-money bdi, .price bdi');
    
    allPriceElements.forEach(priceEl => {
      const currentText = priceEl.textContent.trim();
      
      if (currentText.includes('$') && /\$\d/.test(currentText)) {
        const numericPrice = parseFloat(currentText.replace(/[$,]/g, ''));
        
        if (numericPrice < 100) return;
        
        let newPrice = (numericPrice >= 100 && numericPrice <= 500) ? newTotalPrice : totalCartPrice;
        
        // DIRECT DOM update - no animations, no delays
        if (priceEl.querySelector('.price__prefix')) {
          const prefix = priceEl.querySelector('.price__prefix');
          priceEl.innerHTML = prefix.outerHTML + newPrice;
        } else {
          priceEl.textContent = `$${newPrice}`;
        }
        

      }
    });
    

  }

  overridePricesInstantly(newQuantity) {
    
    
    // Get actual current prices to understand what we're working with
    const syncedItems = this.querySelectorAll('[data-handle]').forEach(item => {
      const handle = item.dataset.handle;
      if (this.syncedProductHandles.includes(handle)) {
        const priceElements = item.querySelectorAll('price-money bdi, .price bdi');
        priceElements.forEach(priceEl => {
          const currentText = priceEl.textContent.trim();

        });
      }
    });
    
    
  }

  logCurrentPrices() {

    
    // Log prices for each synced product
    this.syncedProductHandles.forEach(handle => {
      const item = this.querySelector(`[data-handle="${handle}"]`);
      if (item) {
        const quantityInput = item.querySelector('[name="updates[]"]');
        const quantity = quantityInput ? quantityInput.value : 'unknown';
        

        
        const priceElements = item.querySelectorAll('price-money bdi, .price bdi');
        priceElements.forEach((priceEl, index) => {
          const currentText = priceEl.textContent.trim();

        });
      }
    });
    
    // Log cart total
    const cartTotalElements = document.querySelectorAll('#mini-cart-subtotal, #mini-cart-subtotal bdi, .cart-total .price bdi, .subtotal .price bdi');
    cartTotalElements.forEach((totalEl, index) => {
      const currentText = totalEl.textContent.trim();

    });
    

  }

  blockThemePriceUpdates() {

    // Theme blocking disabled until we understand what's happening
    return;
  }

  startPriceGuard(correctQuantity) {

    // Price guard disabled until we understand actual product pricing
    return;
  }

  updateSectionsManually(sectionsData) {

    
    // Fetch fresh sections
    const sections = ['mini-cart', 'cart-icon-bubble'];
    const sectionsParam = sections.join(',');
    
    fetch(`/?sections=${sectionsParam}`)
      .then(response => response.json())
      .then(freshSections => {

        
        sections.forEach(sectionId => {
          const element = document.getElementById(sectionId);
          if (element && freshSections[sectionId]) {

            
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = freshSections[sectionId];
            const sectionContent = tempDiv.querySelector('.shopify-section') || tempDiv.firstElementChild;
            
            if (sectionContent) {
              element.innerHTML = sectionContent.innerHTML;
            }
          }
        });
        

      })
      .catch(error => {

      });
  }

  lockSyncedInputs() {

    const cartItems = this.querySelectorAll('[data-handle]');
    
    cartItems.forEach(item => {
      const handle = item.dataset.handle;
      if (this.syncedProductHandles.includes(handle)) {
        const quantityInput = item.querySelector('[name="updates[]"]');
        if (quantityInput) {
          const inputId = quantityInput.id || quantityInput.dataset.index;
          this.lockedInputs.add(inputId);
          // Temporarily disable the input to prevent theme interference
          quantityInput.style.pointerEvents = 'none';
          quantityInput.setAttribute('data-locked', 'true');
        }
      }
    });
  }

  unlockSyncedInputs() {

    const cartItems = this.querySelectorAll('[data-handle]');
    
    cartItems.forEach(item => {
      const handle = item.dataset.handle;
      if (this.syncedProductHandles.includes(handle)) {
        const quantityInput = item.querySelector('[name="updates[]"]');
        if (quantityInput) {
          const inputId = quantityInput.id || quantityInput.dataset.index;
          this.lockedInputs.delete(inputId);
          // Re-enable the input
          quantityInput.style.pointerEvents = '';
          quantityInput.removeAttribute('data-locked');
        }
      }
    });
  }

  forceUISync() {

    
    const cartItems = this.querySelectorAll('[data-handle]');
    const syncedItems = {};
    
    // Find our target products
    cartItems.forEach(item => {
      const handle = item.dataset.handle;
      if (this.syncedProductHandles.includes(handle)) {
        const quantityInput = item.querySelector('[name="updates[]"]');
        if (quantityInput) {
          syncedItems[handle] = {
            element: item,
            input: quantityInput,
            quantity: parseInt(quantityInput.value)
          };
        }
      }
    });
    
    const handles = Object.keys(syncedItems);
    if (handles.length === 2) {
      const [firstHandle, secondHandle] = handles;
      const firstItem = syncedItems[firstHandle];
      const secondItem = syncedItems[secondHandle];
      
      // If quantities don't match, force them to match
      if (firstItem.quantity !== secondItem.quantity) {
        const maxQty = Math.max(firstItem.quantity, secondItem.quantity);
  
        
        // Update values without triggering events to prevent loops
        firstItem.input.value = maxQty;
        secondItem.input.value = maxQty;
        
  
      } else {
  
      }
    }
  }

  reinitializeCartRecommendations() {
    // Don't reload cart recommendations if cart has items - let them stay as they are
    const cartItems = document.querySelectorAll('[data-handle]');

    
    if (cartItems.length > 0) {

      return;
    }
    

    
    // Find all cart-recommendations elements that might need reinitialization
    const cartRecommendations = document.querySelectorAll('cart-recommendations');
    
    cartRecommendations.forEach((element, index) => {

      
      // Check if it has a data-url (needed for fetching recommendations)
      let dataUrl = element.dataset.url;
      if (!dataUrl) {

        return;
      }
      

      
      // Force refresh the URL by adding a timestamp to prevent caching
      const urlObj = new URL(dataUrl, window.location.origin);
      urlObj.searchParams.set('_t', Date.now());
      
      // Also try to get the current first product ID from the cart to ensure fresh recommendations
      const firstCartItem = document.querySelector('[data-handle]');
      if (firstCartItem) {
        const firstProductId = firstCartItem.dataset.productId || firstCartItem.querySelector('[data-product-id]')?.dataset.productId;
        if (firstProductId) {

          urlObj.searchParams.set('product_id', firstProductId);
        }
      }
      
      dataUrl = urlObj.toString();

      
      // Update the element's data-url
      element.dataset.url = dataUrl;
      
      // Always trigger loading for fresh recommendations (don't check hasContent)

      
      // Clear existing content to force reload
      element.innerHTML = '';
      
      // Create a new intersection observer for this element
      const observer = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(element);
        

        
        fetch(dataUrl)
          .then(response => {

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.text();
          })
          .then(text => {

            const html = document.createElement('div');
            html.innerHTML = text;
            const recommendations = html.querySelector('cart-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length) {

              element.innerHTML = recommendations.innerHTML;
              
              // Scroll the mini-cart drawer to the top after recommendations load
              const miniCartMain = document.querySelector('#main-cart-items');
              if (miniCartMain) {
                miniCartMain.scrollTop -= miniCartMain.scrollHeight;
              }
            } else {

              // Log the actual response for debugging

            }
          })
          .catch(e => {


          });
      }, { rootMargin: '0px 0px 100px 0px' }); // More generous margin
      
      // Start observing
      observer.observe(element);

    });
  }

  // Watch for cart recommendations loading completion and update prices
    watchCartRecommendations() {
    // Don't reload cart recommendations if cart has items - let them stay as they are
    const cartItems = this.querySelectorAll('[data-handle]');
    if (cartItems.length > 0) {

      return;
    }
    
    const cartRecommendations = document.querySelector('cart-recommendations');
    if (!cartRecommendations) return;


    
    // Use MutationObserver to watch for content changes in cart recommendations
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.target === cartRecommendations) {
          // Check if recommendations just finished loading (content appeared)
          const hasContent = cartRecommendations.innerHTML.trim().length > 0 && 
                           !cartRecommendations.querySelector('.loading, .spinner');
          
                          if (hasContent) {

                  
                  // Only update prices if cart is empty (no items to sync)
                  const cartItems = document.querySelectorAll('[data-handle]');
                  if (cartItems.length === 0) {

                    setTimeout(() => {
                      this.updateVisiblePrices();
                    }, 300);
                  } else {

                  }

                  // Stop observing once we've handled the first content load
                  observer.disconnect();
                }
        }
      });
    });
    
    // Start observing
    observer.observe(cartRecommendations, {
      childList: true,
      subtree: true
    });
    
    // Auto-disconnect after 10 seconds to prevent memory leaks
    setTimeout(() => {
      observer.disconnect();
    }, 10000);
  }

  disconnectedCallback() {

    // DISABLED: Cart update unsubscriber since we're not subscribing
    // if (this.cartUpdateUnsubscriber) {
    //   this.cartUpdateUnsubscriber();
    // }
    if (this.cartErrorUnsubscriber) {
      this.cartErrorUnsubscriber();
    }
    if (this.cartObserver) {
      this.cartObserver.disconnect();
    }
    if (this.cartCloseObserver) {
      this.cartCloseObserver.disconnect();
    }
  }
}

// Define the custom element
if (!customElements.get('synchronized-products')) {
  customElements.define('synchronized-products', SynchronizedProducts);

} else {

} 
